import { Box, Button, Grid2 as Grid, Switch, TextField, Typography, useTheme } from '@mui/material';
import { FormikErrors, FormikHelpers, FormikTouched, useFormik } from 'formik';
import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { OnChangeValue } from 'react-select';

import { Loader } from '../../../components';
import {
  ADDRESS1,
  ADDRESS2,
  CITY,
  COMPANY_NAME,
  COUNTRY,
  EMAIL,
  LABEL,
  NEW_ID,
  OrgAddressFilterTypesEnum,
  ORG_ADDRESS_INITIAL_VALUES,
  ORG_ADDRESS_VALIDATION_SCHEMA as validationSchema,
  PHONE,
  STATE,
  STATUS,
  ZIP,
} from '../../../constants/addressBook';
import { addOrgAddressRequest, editOrgAddressRequest } from '../../../store/actions/addressBook';
import { selectOrgAddressById } from '../../../store/selectors/addressBook';
import {
  IAddOrgAddressActionPayload,
  IEditOrgAddressActionPayload,
  IOrgAddress,
  TNewOrgAddress,
} from '../../../types/addressBook';
import { ISelectorValue, NotificationListEnum } from '../../../types/shell';
import { getOrgAddressFormRequiredFieldsByCountryCode } from '../../../utils/addressBook';
import { getRequiredFields } from '../../../utils/form';
import notification from '../../../utils/notification';
import { handleApiError } from '../../../utils/ui';
import { CountrySelector } from '../../_mui';

interface IOrgAddressFormProps {
  id: string;
  onClose?: () => void;
}
const OrgAddressForm: React.FC<IOrgAddressFormProps> = ({ id, onClose }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const address = useSelector(selectOrgAddressById(id));

  const isAddMode = id === NEW_ID;

  const OrgAddressSuccessToastMessages = (isNew: boolean) => {
    return isNew ? 'New Address was added' : 'Address was edited';
  };

  const dispatchSubmit = useCallback(
    (payload: IAddOrgAddressActionPayload | IEditOrgAddressActionPayload) => {
      switch (isAddMode) {
        case true:
          return dispatch(addOrgAddressRequest(payload as IAddOrgAddressActionPayload));
        case false:
          return dispatch(editOrgAddressRequest(payload as IEditOrgAddressActionPayload));
        default:
          return null;
      }
    },
    [dispatch, isAddMode],
  );

  const handleSubmit = useCallback(
    async (values: Partial<IOrgAddress | TNewOrgAddress>, { setSubmitting }: FormikHelpers<Partial<IOrgAddress>>) => {
      const castedValues = validationSchema.cast(values);
      await new Promise((resolve, reject) => {
        const addressPayload = isAddMode
          ? (({ status, uid, ...rest }) => rest)(castedValues as Partial<IOrgAddress | TNewOrgAddress>)
          : castedValues;
        dispatchSubmit({ address: addressPayload as Partial<IOrgAddress | TNewOrgAddress>, resolve, reject });
      })
        .then(() => {
          onClose?.();
          notification.success(NotificationListEnum.Success, {
            content: OrgAddressSuccessToastMessages(isAddMode),
          });
        })
        .catch(handleApiError("Something bad happened. Address wasn't saved."))
        .finally(() => {
          setSubmitting(false);
        });
    },
    [onClose, dispatchSubmit, isAddMode, validationSchema],
  );

  const form = useFormik<Partial<IOrgAddress>>({
    initialValues: address ? address : ORG_ADDRESS_INITIAL_VALUES,
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    validateOnMount: false,
  });

  const title = useMemo(() => {
    return isAddMode ? 'Add new address' : 'Edit address';
  }, [isAddMode]);

  const handleSelectInputChange = useCallback(
    (name: keyof IOrgAddress, selected?: OnChangeValue<ISelectorValue, boolean> | null) => {
      const { value } = (selected as ISelectorValue) || {};
      return form.setFieldValue(name, value);
    },
    [form.setFieldValue],
  );

  const getFieldErrors = useCallback(
    (fieldName: keyof IOrgAddress) => {
      const touched: FormikTouched<IOrgAddress> = form.touched;
      const errors: FormikErrors<IOrgAddress> = form.errors;

      return touched[fieldName] ? (errors[fieldName] as string | string[]) : undefined;
    },
    [form.touched, form.errors],
  );

  const requiredFields = useMemo(
    () => getRequiredFields(validationSchema, getOrgAddressFormRequiredFieldsByCountryCode(form?.values[COUNTRY])),
    [validationSchema, form?.values[COUNTRY]],
  );

  const handleToggleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      form.setFieldValue(
        STATUS,
        e.target.checked ? OrgAddressFilterTypesEnum.Active : OrgAddressFilterTypesEnum.NotActive,
      );
    },
    [form.setFieldValue],
  );

  const textFieldStyles = useMemo(() => {
    return {
      '& .MuiInputLabel-root': {
        color: theme.sass.ebony,
      },
    };
  }, [theme.sass.ebony]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {form.isSubmitting && <Loader isLoading={form.isSubmitting} />}
      <form onSubmit={form.handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Grid container spacing={7}>
          <Grid container spacing={3} size={12}>
            <Grid size={12}>
              <Typography variant="h6" sx={{ lineHeight: 1.4 }}>
                {title}
              </Typography>
            </Grid>
            <Grid size={12}>
              <TextField
                {...form.getFieldProps(LABEL)}
                label="Label"
                name={LABEL}
                placeholder="Label"
                required={requiredFields[LABEL]}
                error={!!getFieldErrors(LABEL)}
                helperText={getFieldErrors(LABEL)}
                size="medium"
                fullWidth
                data-action="input_label"
                sx={{ ...textFieldStyles }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                {...form.getFieldProps(COMPANY_NAME)}
                label="Company name"
                name={COMPANY_NAME}
                placeholder="Company name"
                required={requiredFields[COMPANY_NAME]}
                error={!!getFieldErrors(COMPANY_NAME)}
                helperText={getFieldErrors(COMPANY_NAME)}
                size="medium"
                fullWidth
                data-action="input_company_name"
                sx={{ ...textFieldStyles }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={3} size={12}>
            <Grid size={12}>
              <TextField
                {...form.getFieldProps(ADDRESS1)}
                label="Address 1"
                name={ADDRESS1}
                placeholder="Address 1"
                required={requiredFields[ADDRESS1]}
                error={!!getFieldErrors(ADDRESS1)}
                helperText={getFieldErrors(ADDRESS1)}
                size="medium"
                fullWidth
                data-action="input_address1"
                sx={{ ...textFieldStyles }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                {...form.getFieldProps(ADDRESS2)}
                label="Address 2"
                name={ADDRESS2}
                placeholder="Address 2"
                required={requiredFields[ADDRESS2]}
                error={!!getFieldErrors(ADDRESS2)}
                helperText={getFieldErrors(ADDRESS2)}
                size="medium"
                fullWidth
                data-action="input_address2"
                sx={{ ...textFieldStyles }}
              />
            </Grid>
            <Grid container spacing={2}>
              <Grid size={4.5}>
                <TextField
                  {...form.getFieldProps(CITY)}
                  label="City"
                  name={CITY}
                  placeholder="City"
                  required={requiredFields[CITY]}
                  error={!!getFieldErrors(CITY)}
                  helperText={getFieldErrors(CITY)}
                  size="medium"
                  fullWidth
                  data-action="input_city"
                  sx={{ ...textFieldStyles }}
                />
              </Grid>
              <Grid size={4.5}>
                <TextField
                  {...form.getFieldProps(STATE)}
                  label="State"
                  name={STATE}
                  placeholder="State"
                  required={requiredFields[STATE]}
                  error={!!getFieldErrors(STATE)}
                  helperText={getFieldErrors(STATE)}
                  size="medium"
                  fullWidth
                  data-action="input_state"
                  sx={{ ...textFieldStyles }}
                />
              </Grid>
              <Grid size={3}>
                <TextField
                  {...form.getFieldProps(ZIP)}
                  label="ZIP code"
                  name={ZIP}
                  placeholder="ZIP code"
                  required={requiredFields[ZIP]}
                  error={!!getFieldErrors(ZIP)}
                  helperText={getFieldErrors(ZIP)}
                  size="medium"
                  fullWidth
                  data-action="input_zip"
                  sx={{ ...textFieldStyles }}
                />
              </Grid>
            </Grid>
            <Grid size={12}>
              <CountrySelector
                label="Country"
                name={COUNTRY}
                required={requiredFields[COUNTRY]}
                value={form.values[COUNTRY]}
                onChange={handleSelectInputChange}
                error={getFieldErrors(COUNTRY)}
              />
            </Grid>
          </Grid>
          <Grid container spacing={4} size={12}>
            <Grid size={12}>
              <TextField
                {...form.getFieldProps(PHONE)}
                label="Phone"
                name={PHONE}
                placeholder="Phone"
                required={requiredFields[PHONE]}
                error={!!getFieldErrors(PHONE)}
                helperText={getFieldErrors(PHONE)}
                size="medium"
                fullWidth
                data-action="input_phone"
                sx={{ ...textFieldStyles }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                {...form.getFieldProps(EMAIL)}
                label="Email"
                name={EMAIL}
                placeholder="Email"
                required={requiredFields[EMAIL]}
                error={!!getFieldErrors(EMAIL)}
                helperText={getFieldErrors(EMAIL)}
                size="medium"
                fullWidth
                data-action="input_email"
                sx={{ ...textFieldStyles }}
              />
            </Grid>
            {!isAddMode && (
              <Grid size={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1">Status</Typography>
                <Switch
                  checked={form.values[STATUS] === OrgAddressFilterTypesEnum.Active}
                  onChange={handleToggleChange}
                  data-action="input_status"
                />
              </Grid>
            )}
          </Grid>
        </Grid>
        <Box sx={{ display: 'flex', justifyContent: 'end', gap: 2, mt: !isAddMode ? 3 : 'auto', flex: 0 }}>
          <Button variant="text" onClick={onClose} data-action="cancel_btn">
            Cancel
          </Button>
          <Button variant="contained" onClick={form.submitForm} data-action="submit_btn" disabled={form.isSubmitting}>
            Save
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default OrgAddressForm;
